import { useEffect, useMemo, useRef, useState } from 'react';
// MIGRATION: fully on AWS PostgREST (save_po RPC + po_attachments). Phase-1
// locked-table writes still go through authWrite Lambda.
import { storage } from '../lib/awsStorage';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { ensureContact } from '../lib/contacts';
import ContactAutocomplete from '../lib/ContactAutocomplete';
import { formatMoney, formatBytes } from '../lib/money';
import { logPoActivity, formatPoActivityWhen, humanInvoiceAction, humanPoAction } from '../lib/poActivityLog';
import { fileToBase64 } from '../lib/fileEncoding';
import FilePreviewModal from './FilePreviewModal';
import './drawer.css';

const ACCEPTED_MIME = ['application/pdf', 'image/png', 'image/jpeg'];
const BUCKET = 'po-attachments';

// PO number format — matches migration 108's CHECK constraint and the
// authWrite Lambda regex. Single source of truth on the frontend.
const PO_NUMBER_RE = /^PO-[0-9]{3,8}$/;
function normalisePoNumber(raw) {
  if (raw == null) return '';
  let s = String(raw).trim().toUpperCase();
  if (!s) return '';
  // Strip any "PO" prefix variant ("PO-", "PO_", "PO ", "PO#", "PO ").
  s = s.replace(/^P[O0]\s*[-_/#.\s]?\s*/, '');
  // Keep digits only — supports "PO-461", "461", "po 0461", "PO_0451".
  const digits = s.replace(/\D+/g, '');
  if (!digits) return '';
  // Pad to 4 digits when shorter (Mo's existing convention is PO-0451).
  // Numbers with 5+ digits stay as-is (future-proof past PO-9999).
  const padded = digits.length < 4 ? digits.padStart(4, '0') : digits;
  return `PO-${padded}`;
}

const TABS = [
  { key: 'comments', label: 'Comments' },
  { key: 'edit', label: 'Edit' },
  { key: 'lines', label: 'Line Items' },
];

const MONO_FONT = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

// LineDescriptionPicker — dropdown sourced from forecaster_catalog with an
// "Other" escape hatch for one-offs (freight, services, anything not in
// the SKU list). Per Ace 2026-04-28: standardise the description string
// across POs so reports/extracts don't get noisy variants of the same SKU.
//
// Modes:
//   'sku'    — show <select> of SKUs + "Other"; description = picked sku_name
//   'custom' — show <input type=text> + small back-arrow to return to dropdown
//
// Mode is inferred on first render from whether the existing description
// matches a known SKU name. After that, the parent passes _mode explicitly
// so the user's choice ("Other" → custom) is sticky even if they clear the
// text. _mode is UI-only and not persisted (save logic only sends
// description/qty/unit_cost — see lines ~263–270).
const OTHER_SENTINEL = '__OTHER__';
function LineDescriptionPicker({ value, mode, skus, onChange }) {
  const isCustom = mode === 'custom' || (mode == null && value && !skus.some(s => s.sku_name === value));
  if (isCustom) {
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange({ description: e.target.value, _mode: 'custom' })}
          placeholder="Description (free text)"
          style={{ flex: 1, minWidth: 0 }}
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          style={{ padding: '0 8px', fontSize: 11 }}
          title="Back to SKU list"
          onClick={() => onChange({ description: '', _mode: 'sku' })}
        >↩</button>
      </div>
    );
  }
  return (
    <select
      value={value || ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === OTHER_SENTINEL) {
          onChange({ description: '', _mode: 'custom' });
        } else {
          onChange({ description: v, _mode: 'sku' });
        }
      }}
    >
      <option value="" disabled>Pick an item…</option>
      {skus.map((s) => (
        // sku_code as title (tooltip) so the option text stays compact;
        // the closed select is width-constrained by its grid cell.
        <option key={s.id} value={s.sku_name} title={s.sku_code || undefined}>{s.sku_name}</option>
      ))}
      <option value={OTHER_SENTINEL}>Other (type your own)</option>
    </select>
  );
}

// Single attachment zone: dropzone + file list, scoped by kind. Sales
// Order uses one of these; the original PO PDF uses another. Sharing
// the layout keeps the visual treatment identical so the eye reads
// them as peers, not as a primary + secondary.
function AttachmentsZone({
  label, hint, kind, attachments,
  dragover, setDragover, fileRef,
  onUpload, onPreview, onRemove,
}) {
  const readOnly = !onUpload;
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {hint ? <div style={{ fontSize: 11, color: '#9A9B9E', marginTop: -4, marginBottom: 6 }}>{hint}</div> : null}
      {!readOnly && (
        <>
          <div className={`attach-dropzone ${dragover ? 'dragover' : ''}`}
            onClick={() => fileRef?.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={e => { e.preventDefault(); setDragover(false); onUpload([...(e.dataTransfer?.files || [])]); }}
          >
            Drop {label.toLowerCase()} here or click to upload<br />
            <span className="muted-xs">PDF, PNG, JPG</span>
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" multiple hidden
            onChange={e => { onUpload([...(e.target.files || [])]); e.target.value = ''; }} />
        </>
      )}
      {attachments.length > 0 && (
        <div className="attach-file-list">
          {attachments.map(a => (
            <div key={a.id} className="attach-file-row">
              <a href="#" className="attach-file-name" onClick={e => { e.preventDefault(); onPreview(a); }}>{a.filename}</a>
              <span className="attach-file-size">{formatBytes(a.size_bytes)}</span>
              <button type="button" className="file-eye-btn" onClick={() => onPreview(a)} title="Preview">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/></svg>
              </button>
              <button type="button" className="icon-del" onClick={() => onRemove(a)} title="Remove">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PoStat({ label, value, sub, accent }) {
  return (
    <div>
      <div style={{ fontFamily: MONO_FONT, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', color: accent ? '#B85C3C' : '#0F1419' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#5C6166', marginTop: 2, fontFamily: MONO_FONT }}>{sub}</div>}
    </div>
  );
}

export default function PoDrawer({
  session, po = null, existingPoNumbers = [], suppliers = [],
  onClose, onSaved, onDeleted, onPoStatusChanged,
  poLineItems = [], poActivity = [], invoiceActivity = [],
  poComments = [], invoiceLines = [], linkedInvoices = [],
  poBilled = 0, poPaid = 0
}) {
  const isEdit = !!po?.id;
  const [tab, setTab] = useState(isEdit ? 'comments' : 'edit');
  const [form, setForm] = useState(() => po ? {
    po_number: po.po_number || '', order_date: po.order_date || '',
    supplier_name: po.supplier_name || '', currency: po.currency || 'AUD',
    total_amount: po.total_amount || '', tax: '', notes: po.notes || '',
    tr_number: po.tr_number || '', packing_slip_number: po.packing_slip_number || '',
    sales_order_number: po.sales_order_number || '',
    // Date input wants YYYY-MM-DD; Postgres stores tz timestamp.
    sales_order_received_at: po.sales_order_received_at
      ? new Date(po.sales_order_received_at).toISOString().slice(0, 10)
      : '',
  } : {
    po_number: '', order_date: '', supplier_name: '', currency: 'AUD',
    total_amount: '', tax: '', notes: '',
    tr_number: '', packing_slip_number: '',
    sales_order_number: '', sales_order_received_at: '',
  });
  const [lines, setLines] = useState([]);
  const [linesLoaded, setLinesLoaded] = useState(!isEdit);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  // Two upload zones share state but track drag/click per zone so the
  // hover ring only highlights the active one.
  const [dragoverPo, setDragoverPo] = useState(false);
  const [dragoverSo, setDragoverSo] = useState(false);
  const fileRefPo = useRef(null);
  const fileRefSo = useRef(null);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting] = useState(false);
  const [newSupOpen, setNewSupOpen] = useState(false);
  const [newSupName, setNewSupName] = useState('');
  const [newSupError, setNewSupError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [autoFilled, setAutoFilled] = useState({});   // keys that were auto-filled
  const [pendingFile, setPendingFile] = useState(null); // file to auto-attach after save

  // SKU catalogue loaded once for the line-item description picker.
  // Ace's request 2026-04-28: standardise the description field by sourcing
  // it from the maintained SKU catalogue with an "Other" escape hatch for
  // one-offs (freight, services, anything not in catalogue).
  const [skus, setSkus] = useState([]);
  useEffect(() => {
    aws.from('forecaster_catalog')
      .select('id, sku_name, sku_code')
      .eq('is_active', true)
      .neq('status', 'retired')
      .order('sku_name', { ascending: true })
      .then(({ data }) => setSkus(data || []));
  }, []);
  const extractRef = useRef(null);

  const isAdmin = session?.role === 'admin';
  const isLocked = po?.locked === true;
  const isClosed = po?.status === 'closed';
  const editDisabled = isLocked && isEdit;

  function clearAutoFill(key) { setAutoFilled(af => { const n = { ...af }; delete n[key]; return n; }); }
  function setFieldAuto(k, v) { setForm(f => ({ ...f, [k]: v })); setAutoFilled(af => ({ ...af, [k]: true })); }

  async function extractPo(file) {
    setExtracting(true);
    setError('');
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch(import.meta.env.VITE_AWS_EXTRACT_PO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: base64, mime_type: file.type, userPin: session?.code })
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.error || 'Extraction failed'); setExtracting(false); return; }
      const d = await res.json();
      if (d.po_number) setFieldAuto('po_number', String(d.po_number));
      if (d.supplier) setFieldAuto('supplier_name', d.supplier);
      if (d.date_issued) setFieldAuto('order_date', d.date_issued);
      if (d.currency) setFieldAuto('currency', d.currency);
      if (d.total != null) setFieldAuto('total_amount', String(d.total));
      if (d.tax_percent != null) setFieldAuto('tax', String(d.tax_percent));
      if (d.notes) setFieldAuto('notes', d.notes);
      if (Array.isArray(d.line_items) && d.line_items.length > 0) {
        setLines(d.line_items.map(li => ({
          _k: Math.random().toString(36).slice(2, 8),
          description: li.description || '',
          qty_ordered: li.qty != null ? String(li.qty) : '',
          unit_cost: li.unit_cost != null ? String(li.unit_cost) : ''
        })));
        setAutoFilled(af => ({ ...af, lines: true }));
      }
      setPendingFile(file);
    } catch (err) {
      setError('Extraction failed: ' + err.message);
    }
    setExtracting(false);
  }

  useEffect(() => {
    if (!isEdit) return;
    aws.from('po_line_items').select('*').eq('po_id', po.id).then(({ data }) => {
      setLines((data || []).map(l => ({ ...l, _k: l.id })));
      setLinesLoaded(true);
    });
    aws.from('po_attachments').select('*').eq('po_id', po.id).order('created_at', { ascending: false }).then(({ data }) => setAttachments(data || []));
  }, [isEdit, po?.id]);

  // ── Unified feed: activity + comments ──
  const invNumMap = useMemo(() => {
    const m = {};
    linkedInvoices.forEach(i => { m[i.id] = i.invoice_number || ''; });
    return m;
  }, [linkedInvoices]);

  const unifiedFeed = useMemo(() => {
    const items = [];
    poActivity.forEach(a => items.push({
      ...a, _type: 'activity', _display: humanPoAction(a), _invNum: null
    }));
    invoiceActivity.forEach(a => {
      const invNum = invNumMap[a.invoice_id] || '';
      const result = humanInvoiceAction(a, invNum);
      items.push({
        id: a.id, created_at: a.created_at, actor_name: a.actor_name,
        _type: 'activity', _display: result.text, _invNum: result.invNum
      });
    });
    poComments.forEach(c => items.push({ ...c, _type: 'comment', actor_name: c.author_name }));
    return items.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [poActivity, invoiceActivity, poComments, invNumMap]);

  // ── Variance data for Line Items tab ──
  const lineVariance = useMemo(() => {
    const map = {};
    poLineItems.forEach(pl => { map[pl.id] = { ...pl, invoiced: 0 }; });
    invoiceLines.forEach(il => {
      if (il.po_line_item_id && map[il.po_line_item_id]) {
        map[il.po_line_item_id].invoiced += Number(il.qty_invoiced || 0);
      }
    });
    return map;
  }, [poLineItems, invoiceLines]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const linesTotal = lines.reduce((s, l) => s + (Number(l.qty_ordered || 0) * Number(l.unit_cost || 0)), 0);
  const hasLines = lines.length > 0;
  const taxPct = Number(form.tax || 0);
  const taxAmount = hasLines ? Math.round(linesTotal * taxPct) / 100 : 0;
  const effectiveTotal = hasLines ? linesTotal + taxAmount : Number(form.total_amount || 0);

  const addLine = () => setLines(ls => [...ls, { _k: Math.random().toString(36).slice(2, 8), description: '', qty_ordered: '', unit_cost: '' }]);
  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  function addGst() { setField('tax', '10'); }

  async function addNewSupplier() {
    setNewSupError('');
    const name = newSupName.trim();
    if (!name) return setNewSupError('Supplier name is required.');
    if (suppliers.some(s => s.name.toLowerCase() === name.toLowerCase())) {
      setField('supplier_name', suppliers.find(s => s.name.toLowerCase() === name.toLowerCase()).name);
      setNewSupOpen(false); setNewSupName(''); return;
    }
    let inserted;
    try {
      inserted = await authWrite(session, 'suppliers', 'insert', { name });
    } catch (e) {
      return setNewSupError(e.message);
    }
    const created = Array.isArray(inserted) ? inserted[0] : null;
    if (!created) return setNewSupError('No row returned');
    setField('supplier_name', created.name);
    setNewSupOpen(false); setNewSupName('');
  }

  async function postComment() {
    if (!commentText.trim() || !po?.id) return;
    setPosting(true);
    await authWrite(session, 'po_comments', 'insert', {
      po_id: po.id, author_code: session.code, author_name: session.name, body: commentText.trim(),
    });
    setCommentText('');
    setPosting(false);
    onSaved?.();
  }

  // kind: 'po' | 'sales_order' | 'packing_slip' | 'other'. The PO
  // upload zone passes 'po'; the Sales Order zone passes 'sales_order'.
  // Defaults to 'po' for legacy callers.
  async function uploadFiles(files, kind = 'po') {
    for (const file of files) {
      if (!ACCEPTED_MIME.includes(file.type)) continue;
      const key = `${po.id}/${Date.now()}-${file.name}`;
      const { error: upErr, data: upData } = await storage.from(BUCKET).upload(key, file, { contentType: file.type, upsert: false });
      if (upErr) { console.error(upErr); continue; }
      const storedPath = upData?.path || key;
      try {
        await authWrite(session, 'po_attachments', 'insert', {
          po_id: po.id, storage_path: storedPath, filename: file.name,
          mime_type: file.type, size_bytes: file.size, uploaded_by: session?.code,
          kind,
        });
      } catch (rowErr) {
        await storage.from(BUCKET).remove([storedPath]);
        console.error(rowErr);
        continue;
      }
      const verb = kind === 'sales_order' ? 'Attached sales order' : 'Attached file';
      await logPoActivity(po.id, session, verb, file.name);
    }

    // Side-effect: when a sales order lands, stamp sales_order_received_at
    // = now() if it isn't already set. Mo's natural mental model: "the
    // moment I dropped the SO PDF in is when the supplier acknowledged".
    // Doesn't overwrite an explicit date the user already typed.
    if (kind === 'sales_order' && !po?.sales_order_received_at && !form.sales_order_received_at) {
      try {
        await authWrite(session, 'purchase_orders', 'update',
          { sales_order_received_at: new Date().toISOString() },
          { column: 'id', value: po.id });
        setField('sales_order_received_at', new Date().toISOString().slice(0, 10));
      } catch (e) {
        console.warn('SO received-at auto-stamp failed:', e?.message);
      }
    }

    const { data } = await aws.from('po_attachments').select('*').eq('po_id', po.id).order('created_at', { ascending: false });
    setAttachments(data || []);
    onSaved?.();
  }

  const [preview, setPreview] = useState(null); // { url, filename }

  async function previewFile(storagePath, filename, bucket = BUCKET) {
    const { data, error } = await storage.from(bucket).createSignedUrl(storagePath, 3600);
    if (error || !data?.signedUrl) { alert('Could not open file'); return; }
    setPreview({ url: data.signedUrl, filename });
  }

  async function removeAttachment(att) {
    if (!confirm(`Remove ${att.filename}?`)) return;
    await authWrite(session, 'po_attachments', 'delete', null, { column: 'id', value: att.id });
    await storage.from(BUCKET).remove([att.storage_path]);
    await logPoActivity(po.id, session, 'Removed file', att.filename);
    const { data } = await aws.from('po_attachments').select('*').eq('po_id', po.id).order('created_at', { ascending: false });
    setAttachments(data || []);
    onSaved?.();
  }

  async function save() {
    setError('');
    // Auto-normalise PO number: bare digits → "PO-XXXX" (zero-padded
    // to 4). Mirrors the server-side CHECK constraint in mig 108 and
    // the authWrite Lambda regex. Closes the dup-entry hole Ace found
    // (PO_0451 vs PO-0451 used to slip through the existing string
    // match — now both normalise to the same canonical form).
    const po_number = normalisePoNumber(form.po_number);
    if (po_number !== form.po_number) setField('po_number', po_number);
    if (po_number && !PO_NUMBER_RE.test(po_number)) {
      return setError('PO number must be in the format PO-NNNN (3–8 digits, e.g. PO-0451).');
    }
    const supplier_name = form.supplier_name.trim();
    if (!po_number) return setError('PO number is required.');
    if (!supplier_name) return setError('Supplier is required.');
    // Dedup check normalises both sides so "PO-0451" and "PO_0451"
    // collide as the same number.
    const existingNorm = (existingPoNumbers || [])
      .map(n => normalisePoNumber(n))
      .filter(Boolean);
    if (!isEdit && existingNorm.includes(po_number)) return setError(`PO #${po_number} already exists.`);
    if (isEdit && po_number !== normalisePoNumber(po.po_number) && existingNorm.includes(po_number)) return setError(`PO #${po_number} already exists.`);
    setSaving(true);

    const poData = {
      po_number,
      order_date: form.order_date || null,
      supplier_name,
      currency: form.currency,
      total_amount: String(effectiveTotal),
      notes: form.notes || null
    };

    const lineData = lines
      .filter(l => l.description || l.qty_ordered || l.unit_cost)
      .map(l => ({
        description: l.description || null,
        qty_ordered: Number(l.qty_ordered) || 0,
        unit_cost: Number(l.unit_cost) || 0
      }));

    const { data: result, error: rpcErr } = await aws.rpc('save_po', {
      p_po_id: isEdit ? po.id : null,
      p_data: poData,
      p_lines: lineData,
      p_actor_code: session.code,
      p_actor_name: session.name,
      p_expected_updated_at: isEdit ? po.updated_at : null
    });

    if (rpcErr) {
      setSaving(false);
      if (rpcErr.message?.includes('Conflict')) {
        return setError('This PO was modified by someone else. Close and reopen to get the latest version.');
      }
      return setError(rpcErr.message);
    }

    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    const poId = parsed?.id || po?.id;

    // TR No / Packing Slip No / Sales Order — `save_po` RPC doesn't
    // accept these columns (predates the migrations that added them).
    // Patch them in via a follow-up authWrite update so the table can
    // show them. Skip the round-trip entirely when nothing changed.
    const trChanged = (form.tr_number || '').trim() !== (po?.tr_number || '');
    const psChanged = (form.packing_slip_number || '').trim() !== (po?.packing_slip_number || '');
    const soNumberChanged = (form.sales_order_number || '').trim() !== (po?.sales_order_number || '');
    const priorSoDate = po?.sales_order_received_at
      ? new Date(po.sales_order_received_at).toISOString().slice(0, 10)
      : '';
    const soDateChanged = (form.sales_order_received_at || '') !== priorSoDate;
    if (poId && (trChanged || psChanged || soNumberChanged || soDateChanged)) {
      try {
        const patch = {
          tr_number:           form.tr_number?.trim() || null,
          packing_slip_number: form.packing_slip_number?.trim() || null,
          sales_order_number:  form.sales_order_number?.trim() || null,
          // Stored as midday-local-time so the date doesn't drift across
          // timezone boundaries (matches the Last-spoken pattern in
          // RelationshipDrawer).
          sales_order_received_at: form.sales_order_received_at
            ? new Date(`${form.sales_order_received_at}T12:00:00`).toISOString()
            : null,
        };
        await authWrite(session, 'purchase_orders', 'update', patch, { column: 'id', value: poId });
      } catch (e) {
        // Non-fatal — the PO is saved; only the supplementary fields failed.
        console.warn('TR/PS/SO update failed', e);
      }
    }

    // File attachment stays separate. The pendingFile path is only
    // hit on initial save (the file Mo drops to trigger extract-po) —
    // always tag it kind='po'. Sales-order uploads happen post-save
    // through the dedicated zone in the Attachments section.
    if (pendingFile) {
      const key = `${poId}/${Date.now()}-${pendingFile.name}`;
      const { error: upErr, data: upData } = await storage.from(BUCKET).upload(key, pendingFile, { contentType: pendingFile.type, upsert: false });
      if (!upErr) {
        const storedPath = upData?.path || key;
        await authWrite(session, 'po_attachments', 'insert', {
          po_id: poId, storage_path: storedPath, filename: pendingFile.name,
          mime_type: pendingFile.type, size_bytes: pendingFile.size, uploaded_by: session?.code,
          kind: 'po',
        });
        await logPoActivity(poId, session, 'Attached file', pendingFile.name);
      }
    }

    ensureContact(form.supplier_name);
    setSaving(false);
    onSaved?.();
    onClose();
  }

  async function closePo() {
    // Stamp the current status into previous_status before flipping
    // to 'closed' so reopen can restore it. Don't overwrite a previous
    // value if (somehow) the PO is already closed. Mig 129.
    await authWrite(session, 'purchase_orders', 'update', {
      status: 'closed',
      previous_status: po.status === 'closed' ? po.previous_status : po.status,
    }, { column: 'id', value: po.id });
    await logPoActivity(po.id, session, 'Closed PO', `PO #${po.po_number}`);
    // UX: hint the parent which tab to flip to so Mo sees where the PO
    // landed instead of staring at an empty list. Side-pane closes
    // immediately; parent reload + tab swap happens in the background.
    onPoStatusChanged?.('closed');
    onSaved?.(); onClose();
  }
  async function reopenPo() {
    // Restore the prior status. CHECK constraint on `status` does NOT
    // include 'open' — that was the 2026-05-07 reopen-button 500.
    // Fall back to 'sent' for POs closed before mig 129 (no
    // previous_status saved) — most-likely past lifecycle stage.
    const restored = po.previous_status || 'sent';
    await authWrite(session, 'purchase_orders', 'update', {
      status: restored,
      previous_status: null,
    }, { column: 'id', value: po.id });
    await logPoActivity(po.id, session, 'Reopened PO', `PO #${po.po_number} → ${restored}`);
    onPoStatusChanged?.('open');
    onSaved?.(); onClose();
  }
  async function lockPo() {
    await authWrite(session, 'purchase_orders', 'update', { locked: true, locked_by: session.name }, { column: 'id', value: po.id });
    await logPoActivity(po.id, session, 'Locked PO', `by ${session.name}`);
    onSaved?.(); onClose();
  }
  async function unlockPo() {
    await authWrite(session, 'purchase_orders', 'update', { locked: false, locked_by: null }, { column: 'id', value: po.id });
    await logPoActivity(po.id, session, 'Unlocked PO', `by ${session.name}`);
    onSaved?.(); onClose();
  }
  async function deletePo() {
    if (!isAdmin) return;
    const poLabel = /^PO/i.test(po.po_number) ? po.po_number : `PO #${po.po_number}`;
    if (!confirm(`Archive ${poLabel}? It will move to the Archive tab.`)) return;
    try {
      await authWrite(session, 'purchase_orders', 'update', { archived_at: new Date().toISOString(), archived_by: session.name }, { column: 'id', value: po.id });
    } catch (err) { alert('Archive failed: ' + err.message); return; }
    onDeleted?.(); onClose();
  }

  function renderActivityText(text, invNum) {
    if (!invNum || !text.includes(invNum)) return text;
    const idx = text.indexOf(invNum);
    return <>{text.slice(0, idx)}<strong>{invNum}</strong>{text.slice(idx + invNum.length)}</>;
  }

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer po-drawer">
        <div className="drawer-header">
          <div>
            <div style={{ fontFamily: MONO_FONT, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 4 }}>Purchase Order</div>
            <div className="drawer-title" style={{ marginBottom: 2 }}>
              {isEdit ? <>{/^PO/i.test(po.po_number) ? po.po_number : `PO #${po.po_number}`}{isLocked && <span className="lock-icon" title={`Locked by ${po.locked_by || 'admin'}`}> &#x1F512;</span>}</> : 'Log Purchase Order'}
            </div>
            {isEdit && po?.supplier_name && <div style={{ fontSize: 13, color: '#5C6166' }}>{po.supplier_name}</div>}
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {isEdit && (() => {
          const total = Number(po?.total_amount || 0);
          const outstanding = Math.max(0, total - poPaid);
          const cur = po?.currency || 'AUD';
          const isAud = cur === 'AUD';
          return (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #EFEDE7', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <PoStat label="Total"
                value={formatMoney(total)}
                sub={isAud ? null : `${cur} ${total.toLocaleString()}`} />
              <PoStat label="Paid"
                value={formatMoney(poPaid)}
                sub={poBilled > 0 && poBilled !== poPaid ? `Billed ${formatMoney(poBilled)}` : null} />
              <PoStat label="Outstanding"
                value={outstanding > 0 ? formatMoney(outstanding) : '—'}
                sub={outstanding > 0 ? null : 'Settled'}
                accent={outstanding > 0} />
            </div>
          );
        })()}

        {isEdit && (
          <div className="po-drawer-tabs">
            {TABS.map(t => (
              <button key={t.key} className={`po-drawer-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>
        )}

        <div className="drawer-content">
          {/* ── Comments tab ── */}
          {tab === 'comments' && isEdit && (
            <div className="po-comments-tab">
              <div className="po-comment-input">
                <textarea
                  className="form-textarea"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) postComment(); }}
                />
                <button className="btn btn-primary btn-sm" disabled={posting || !commentText.trim()} onClick={postComment}>
                  {posting ? 'Posting...' : 'Post'}
                </button>
              </div>
              <div className="po-unified-feed">
                {unifiedFeed.length === 0 && <div className="po-card-activity-empty">No activity or comments yet</div>}
                {unifiedFeed.map((entry, i) => entry._type === 'comment' ? (
                  <div key={entry.id || i} className="po-feed-comment">
                    <div className="po-feed-comment-header">
                      <span className="po-feed-comment-author">{entry.author_name || 'Someone'}</span>
                      <span className="po-feed-comment-when">{formatPoActivityWhen(entry.created_at)}</span>
                    </div>
                    <div className="po-feed-comment-body">{entry.body}</div>
                  </div>
                ) : (
                  <div key={entry.id || i} className="po-feed-activity">
                    <span className="po-feed-activity-who">{entry.actor_name || 'System'}</span>
                    <span className="po-feed-sep">·</span>
                    <span className="po-feed-activity-what">{renderActivityText(entry._display, entry._invNum)}</span>
                    <span className="po-feed-sep">·</span>
                    <span className="po-feed-activity-when">{formatPoActivityWhen(entry.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Edit tab ── */}
          {(tab === 'edit' || !isEdit) && (<>
            {editDisabled && <div className="lock-banner">This PO is locked. Unlock to make changes.</div>}

            {/* Extract PO drop zone — new PO only */}
            {!isEdit && (
              <div
                className={`po-extract-zone ${extracting ? 'extracting' : ''}`}
                onClick={() => !extracting && extractRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f && ACCEPTED_MIME.includes(f.type)) extractPo(f); }}
              >
                {extracting ? (
                  <span>Reading PO...</span>
                ) : pendingFile ? (
                  <span>Extracted from <strong>{pendingFile.name}</strong> — review and save</span>
                ) : (
                  <span>Drop a PO file to auto-fill <span className="muted-xs">PDF, PNG, JPG</span></span>
                )}
              </div>
            )}
            <input ref={extractRef} type="file" accept="application/pdf,image/png,image/jpeg" hidden onChange={e => { const f = e.target.files?.[0]; if (f) extractPo(f); e.target.value = ''; }} />

            <fieldset disabled={editDisabled} style={{ border: 'none', padding: 0, margin: 0 }}>
              <div className="form-group">
                <label className="form-label">PO Number<span className="required-star">*</span></label>
                <input
                  className={`form-input ${autoFilled.po_number ? 'auto-filled' : ''}`}
                  value={form.po_number}
                  onChange={e => { setField('po_number', e.target.value); clearAutoFill('po_number'); }}
                  onBlur={e => { const n = normalisePoNumber(e.target.value); if (n && n !== e.target.value) setField('po_number', n); }}
                  pattern={PO_NUMBER_RE.source}
                  placeholder="PO-0461"
                  autoFocus
                />
                <div style={{ fontSize: 11, color: 'var(--text-tertiary, #888)', marginTop: 4 }}>
                  Format: PO-NNNN (will auto-prefix &amp; pad on blur).
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Skutopia Transfer No</label>
                <input
                  className="form-input"
                  value={form.tr_number}
                  onChange={e => setField('tr_number', e.target.value)}
                  placeholder="Optional · TR reference"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Packing Slip No</label>
                <input
                  className="form-input"
                  value={form.packing_slip_number}
                  onChange={e => setField('packing_slip_number', e.target.value)}
                  placeholder="Optional · supplier slip number"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Date Issued</label>
                <input type="date" className={`form-input ${autoFilled.order_date ? 'auto-filled' : ''}`} value={form.order_date} onChange={e => { setField('order_date', e.target.value); clearAutoFill('order_date'); }} />
              </div>
              <div className="form-group">
                <label className="form-label">Supplier<span className="required-star">*</span></label>
                <ContactAutocomplete
                  session={session}
                  value={form.supplier_name}
                  onChange={v => { setField('supplier_name', v); clearAutoFill('supplier_name'); }}
                  className={autoFilled.supplier_name ? 'auto-filled' : ''}
                  placeholder="Search or type supplier..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-select" value={form.currency} onChange={e => setField('currency', e.target.value)}>
                  <option value="AUD">AUD</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', fontSize: 'var(--text-2xs)' }}>Line Items</label>
                {lines.length > 0 && (
                  <div className="line-items-table">
                    <div className="po-line-head"><div>Description</div><div style={{ textAlign: 'right' }}>Qty</div><div style={{ textAlign: 'right' }}>Unit Cost</div><div style={{ textAlign: 'right' }}>Line Total</div><div /></div>
                    {lines.map((line, idx) => {
                      const lt = Number(line.qty_ordered || 0) * Number(line.unit_cost || 0);
                      return (
                        <div key={line._k} className="po-line-row">
                          <LineDescriptionPicker
                            value={line.description || ''}
                            mode={line._mode}
                            skus={skus}
                            onChange={(patch) => updateLine(idx, patch)}
                          />
                          <input type="number" step="1" value={line.qty_ordered ?? ''} onChange={e => updateLine(idx, { qty_ordered: e.target.value })} style={{ textAlign: 'right' }} />
                          <input type="number" step="0.01" value={line.unit_cost ?? ''} onChange={e => updateLine(idx, { unit_cost: e.target.value })} style={{ textAlign: 'right' }} />
                          <div className="line-total-cell">{formatMoney(lt)}</div>
                          <button type="button" className="icon-del" onClick={() => removeLine(idx)} title="Remove">×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={addLine}>+ Add line</button>
                {hasLines && <div className="muted-small" style={{ marginTop: 6 }}>Subtotal: {formatMoney(linesTotal)}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Tax / Service Fee (%)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 6 }}>
                    <input type="number" step="0.1" className="form-input" style={{ flex: 1 }} value={form.tax} onChange={e => setField('tax', e.target.value)} placeholder="0" />
                    <span style={{ color: 'var(--text-subdued)', fontSize: 13, flexShrink: 0 }}>%</span>
                  </div>
                  {hasLines && <button type="button" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={addGst}>+ Add GST</button>}
                </div>
                {hasLines && taxPct > 0 && <div className="muted-small" style={{ marginTop: 4 }}>{taxPct}% of {formatMoney(linesTotal)} = {formatMoney(taxAmount)}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Expected Total{hasLines ? <span className="field-hint">subtotal + tax</span> : ''}</label>
                {hasLines ? <div className="readonly-value">{formatMoney(effectiveTotal)}</div>
                  : <input type="number" step="0.01" className="form-input" value={form.total_amount} onChange={e => setField('total_amount', e.target.value)} placeholder="0.00" />}
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Context, payment terms, anything future-you will want." />
              </div>
            </fieldset>

            {isEdit && !editDisabled && (
              <>
                {/* Sales Order metadata (supplier acknowledgement) */}
                <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
                  <legend style={{ fontSize: 11, fontWeight: 600, color: '#6E7278', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, padding: 0 }}>
                    Sales Order (supplier acknowledgement)
                  </legend>
                  <div className="row-2">
                    <div className="form-group">
                      <label className="form-label">SO Number</label>
                      <input className="form-input"
                        value={form.sales_order_number}
                        onChange={e => setField('sales_order_number', e.target.value)}
                        placeholder="e.g. SO-12345"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Received</label>
                      <input className="form-input" type="date"
                        value={form.sales_order_received_at}
                        onChange={e => setField('sales_order_received_at', e.target.value)}
                      />
                    </div>
                  </div>
                </fieldset>

                <AttachmentsZone
                  label="PO PDF"
                  hint="The PO document you sent to the supplier."
                  kind="po"
                  attachments={attachments.filter(a => (a.kind || 'po') === 'po')}
                  dragover={dragoverPo}
                  setDragover={setDragoverPo}
                  fileRef={fileRefPo}
                  onUpload={(files) => uploadFiles(files, 'po')}
                  onPreview={(a) => previewFile(a.storage_path, a.filename)}
                  onRemove={removeAttachment}
                />

                <AttachmentsZone
                  label="Sales Order PDF"
                  hint="The supplier's acknowledgement that came back. Dropping a file auto-stamps the Received date."
                  kind="sales_order"
                  attachments={attachments.filter(a => a.kind === 'sales_order')}
                  dragover={dragoverSo}
                  setDragover={setDragoverSo}
                  fileRef={fileRefSo}
                  onUpload={(files) => uploadFiles(files, 'sales_order')}
                  onPreview={(a) => previewFile(a.storage_path, a.filename)}
                  onRemove={removeAttachment}
                />

                {/* Other attachments (legacy or non-PO/non-SO files). Only
                    rendered when there's actually something there so the
                    drawer doesn't grow an empty section every time. */}
                {attachments.some(a => a.kind && a.kind !== 'po' && a.kind !== 'sales_order') && (
                  <AttachmentsZone
                    label="Other files"
                    hint="Anything that isn't the PO or sales order."
                    kind="other"
                    attachments={attachments.filter(a => a.kind && a.kind !== 'po' && a.kind !== 'sales_order')}
                    dragover={false}
                    setDragover={() => {}}
                    fileRef={null}
                    onUpload={null}
                    onPreview={(a) => previewFile(a.storage_path, a.filename)}
                    onRemove={removeAttachment}
                  />
                )}
              </>
            )}

            {error && <div className="inline-error">{error}</div>}
          </>)}

          {/* ── Line Items tab (read-only with variance) ── */}
          {tab === 'lines' && isEdit && (
            <div className="po-drawer-lines">
              {poLineItems.length === 0 ? (
                <div className="po-card-activity-empty">No line items. Switch to the Edit tab to add them.</div>
              ) : (
                <table className="po-card-lines">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Ordered</th>
                      <th style={{ textAlign: 'right' }}>Invoiced</th>
                      <th style={{ textAlign: 'right' }}>Unit Cost</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poLineItems.map(l => {
                      const v = lineVariance[l.id];
                      const invoiced = v?.invoiced || 0;
                      const ordered = Number(l.qty_ordered || 0);
                      const delta = invoiced - ordered;
                      const hasVar = invoiced > 0 && delta !== 0;
                      return (
                        <tr key={l.id}>
                          <td className="td-truncate" data-tooltip={l.description || undefined}>{l.description || '—'}</td>
                          <td style={{ textAlign: 'right' }}>{ordered}</td>
                          <td style={{ textAlign: 'right' }}>
                            {invoiced || '—'}
                            {hasVar && <span className={`li-variance ${delta > 0 ? 'over' : 'short'}`}> {delta > 0 ? '+' : ''}{delta}</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatMoney(l.unit_cost)}</td>
                          <td style={{ textAlign: 'right' }}>{formatMoney(ordered * Number(l.unit_cost || 0))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Footer.
            Always-visible actions for an existing PO (Delete, Lock,
            Close/Reopen, Cancel) live here regardless of tab — Mo
            flagged 2026-05-07 that he couldn't reopen a closed PO
            because the footer was hidden behind the Comments tab.
            Only Save is edit-tab-only (Save without the form visible
            doesn't make sense).
            For new PO creation (!isEdit) the footer is also always
            visible so Save lives somewhere obvious. */}
        <div className="drawer-footer">
          {isEdit && isAdmin && <button className="btn btn-danger btn-full" onClick={deletePo}>Delete</button>}
          {isEdit && isAdmin && (
            isLocked
              ? <button className="btn btn-secondary btn-full" onClick={unlockPo}>Unlock</button>
              : <button className="btn btn-secondary btn-full" onClick={lockPo}>Lock</button>
          )}
          {isEdit && (
            isClosed
              ? <button className="btn btn-secondary btn-full" onClick={reopenPo}>Reopen</button>
              : <button className="btn btn-secondary btn-full" onClick={closePo}>Close</button>
          )}
          <button className="btn btn-secondary btn-full" onClick={onClose}>Cancel</button>
          {(tab === 'edit' || !isEdit) && !editDisabled && (
            <button className="btn btn-primary btn-full" disabled={saving} onClick={save}>{saving ? 'Saving…' : isEdit ? 'Update' : 'Save PO'}</button>
          )}
        </div>
      </aside>
      {preview && <FilePreviewModal url={preview.url} filename={preview.filename} onClose={() => setPreview(null)} />}
    </>
  );
}
